import React from "react";
import { Polygon, MultiPolygon } from "@turf/helpers";
import { HindcastMetadata } from "@sofarocean/weather-cubes";
import AsyncWeatherProviderProxy from "web-workers/weather-worker/AsyncWeatherProviderProxy";

export type WeatherContextType = {
  getHindcastValue: AsyncWeatherProviderProxy["getHindcastValue"];
  getHindcastTimeSeries: AsyncWeatherProviderProxy["getHindcastTimeSeries"];
  getRequestTimes: AsyncWeatherProviderProxy["getRequestTimes"];
  setWeatherVariables: AsyncWeatherProviderProxy["setWeatherVariables"];
  buildWebGLBuffer: AsyncWeatherProviderProxy["buildWebGLBuffer"];
  generateTilesAsync: AsyncWeatherProviderProxy["generateTiles"];
  setMapRegionOfInterest: (roi: Polygon | MultiPolygon | null) => void;
  setPlotRegionOfInterest: (roi: Polygon | MultiPolygon | null) => void;
  calculateWeatherVariableExtremaLabels: AsyncWeatherProviderProxy["calculateWeatherVariableExtremaLabels"];
  currentMapMetadata: HindcastMetadata;
  currentPlotMetadata: HindcastMetadata;
};

const WeatherContextDefaults: WeatherContextType = {
  getHindcastValue: async () => undefined,
  getHindcastTimeSeries: async () => undefined,
  getRequestTimes: async () => undefined,
  setWeatherVariables: async () => undefined,
  buildWebGLBuffer: async () => "NO_DATA",
  generateTilesAsync: async () => [],
  setMapRegionOfInterest: (roi: Polygon | MultiPolygon | null) => null,
  setPlotRegionOfInterest: (roi: Polygon | MultiPolygon | null) => null,
  calculateWeatherVariableExtremaLabels: async () => undefined,
  currentMapMetadata: {
    downloadError: null,
    downloadStatus: "idle",
    downloadProgress: 0,
    weatherVariables: [],
    hindcastUUID: "",
    initialized: false,
  },
  currentPlotMetadata: {
    downloadError: null,
    downloadStatus: "idle",
    downloadProgress: 0,
    weatherVariables: [],
    hindcastUUID: "",
    initialized: false,
  },
};

/** Provide one-way access to the weather data for consumers of this context
 *  for now. Eventually this should include methods that allow a consumer to
 *  request an update to the weather variables, region of interest, or to
 *  download new forecasts
 */
const WeatherContext = React.createContext<WeatherContextType>(
  WeatherContextDefaults
);

export default WeatherContext;
